/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import {
    getFilterElementsDefault,
    sharpDateFormatter,
    useWorkplaceState,
} from '@finbeat/react-toolkit';
import { getActionLog, getActionLogExcel, getActionLogs } from 'axios/userActionLogApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import { ActionLogInfo, ActionLogRequestFilter, ActionLogTable } from 'models/api/actionLog';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { dateTimeFormatter } from 'utils/columnFormatters';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import './UserActionLog.scss';
import UserActionLogDetailsModal from './UserActionLogDetailsModal';
import UserActionLogFilter from './UserActionLogFilter';

const UserActionLog = () => {
    const { t } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { dateTimeFormat } = useGeneralSettingsContext();

    return (
        <BOWorkplace<ActionLogTable, ActionLogInfo, ActionLogRequestFilter>
            testId="UserActionLog-component"
            pageTitle="BackOfficeUserActionLogs"
            filterParams={{
                sd: getStartOfTheDay(),
                ed: getEndOfTheDay(),
                orderColumn: 'id',
                orderDesc: true,
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey={'BackOfficeId'} />,
                },
                { key: 'type', name: <Trans i18nKey={'BackOfficeType'} /> },
                { key: 'username', name: <Trans i18nKey={'BackOfficeUsername'} /> },
                {
                    key: 'sd',
                    name: <Trans i18nKey={'BackOfficeStartDate'} />,
                    formatter: dateTimeFormatter('sd'),
                },
                {
                    key: 'ed',
                    name: <Trans i18nKey={'BackOfficeEndDate'} />,
                    formatter: dateTimeFormatter('ed'),
                },
                {
                    key: 'methodControllerAction',
                    name: <Trans i18nKey={'BackOfficeMethodControllerAction'} />,
                },
                { key: 'statusCode', name: <Trans i18nKey={'BackOfficeStatusCode'} /> },
            ]}
            storagePath="userActionLog/columns"
            getFilterElements={getFilterElementsDefault([
                {
                    id: 'sd',
                    required: true,
                    formatter: (val) =>
                        `${t('BackOfficeDtFrom')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                {
                    id: 'ed',
                    required: true,
                    formatter: (val) =>
                        `${t('BackOfficeTo')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                { name: t('BackOfficeType'), id: 'type' },
                { name: t('BackOfficeController'), id: 'controller' },
                { name: t('BackOfficeAction'), id: 'action' },
                { name: t('BackOfficeActionParams'), id: 'actionParams' },
                { name: t('BackOfficeStatusCode'), id: 'statusCode' },
                { name: t('BackOfficeResponse'), id: 'response' },
            ])}
            showModal={() => setIsModalVisible(true)}
            filter={<UserActionLogFilter />}
            modal={{
                component: (
                    <UserActionLogDetailsModal
                        isOpen={isModalVisible}
                        toggle={() => setIsModalVisible(false)}
                    />
                ),
                fetchCurrentItemQueryFn: (id) => getActionLog(api, id),
            }}
            fetchQueryFn={(filter) => getActionLogs(api, filter)}
            queryKey={['userActionLog']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getActionLogExcel(api, filter)}
        />
    );
};

const useUserActionLogWorkplaceState = () => useWorkplaceState<ActionLogTable, ActionLogInfo>();

export default UserActionLog;
export { useUserActionLogWorkplaceState };
